import axios from 'axios';
import { ChatMessage } from '@/store';

interface ChatResponse {
  result: string;
}

// Financial symbol detection
const STOCK_SYMBOL_REGEX = /\b[A-Z]{1,5}\b/g;
const FINANCIAL_KEYWORDS = [
  'stock', 'market', 'price', 'trading', 'invest', 'financial', 
  'portfolio', 'etf', 'fund', 'dividend', 'earnings', 'revenue',
  'bull', 'bear', 'trend', 'chart', 'technical', 'fundamental',
  'analyst', 'forecast', 'growth', 'performance', 'volatility'
];

/**
 * Detects if the message contains financial context that would benefit from Alpaca enhancement
 * @param message The message to analyze
 * @returns Object containing whether financial context was detected and any symbols found
 */
export const detectFinancialContext = (message: string): { 
  isFinancial: boolean; 
  symbols: string[] 
} => {
  // Convert to lowercase for keyword matching (but not for symbol detection)
  const lowerMessage = message.toLowerCase();
  
  // Check for financial keywords
  const hasFinancialKeywords = FINANCIAL_KEYWORDS.some(keyword => 
    lowerMessage.includes(keyword)
  );
  
  // Extract potential stock symbols
  const symbolMatches = [...new Set(message.match(STOCK_SYMBOL_REGEX) || [])];
  
  // Filter out common words that might match the symbol pattern but aren't stocks
  const filteredSymbols = symbolMatches.filter(symbol => 
    symbol.length >= 2 && // At least 2 letters
    !['AI', 'ML', 'UI', 'UX', 'API', 'CEO', 'CFO', 'CTO', 'HR', 'IT', 'PM', 'AM', 'JPG', 'PNG', 'PDF', 'DOC', 'XLS', 'CSV', 'TXT', 'MB', 'KB', 'GB'].includes(symbol.toUpperCase()) // Common non-stock acronyms including file extensions and size units
  );
  
  return {
    isFinancial: hasFinancialKeywords || filteredSymbols.length > 0,
    symbols: filteredSymbols
  };
};

export const generateChatResponse = async (message: string, history: { role: string; content: string }[] = []): Promise<string> => {
  try {
    // Detect if the message has financial context
    const { isFinancial, symbols } = detectFinancialContext(message);
    
    // If financial context detected and we have symbols, use the financial LLM
    if (isFinancial && symbols.length > 0) {
      try {
        const financialResponse = await enhancedFinancialResponse(message, symbols);
        if (financialResponse.success && financialResponse.response) {
          return financialResponse.response;
        }
        // If financial response fails, fall back to standard response
        console.log('Financial response failed, falling back to standard LLM');
      } catch (finError) {
        console.error('Error using financial LLM:', finError);
        // Continue to standard response on error
      }
    }
    
    // Standard response path
    const response = await axios.post<ChatResponse>('/api/chat', {
      message,
      history,
    });
    
    return response.data.result || 'Sorry, I could not generate a response.';
  } catch (error) {
    console.error('Error generating chat response:', error);
    
    if (error && typeof error === 'object' && 'response' in error) {
      const err = error as { response?: { data?: { message?: string }, status?: number }, request?: any, message?: string };
      
      if (err.response) {
        console.error('Response error data:', err.response.data);
        console.error('Response error status:', err.response.status);
        return `Error: ${err.response.data?.message || 'Failed to get a valid response'}`;
      } else if (err.request) {
        console.error('No response received:', err.request);
        return 'Error: No response received from the server. Please check your network connection.';
      } else if (err.message) {
        console.error('Error message:', err.message);
        return `Error: ${err.message}`;
      }
    }
    
    return 'An unexpected error occurred. Please try again later.';
  }
};

export const mockChatResponse = async (message: string): Promise<string> => {
  await new Promise(resolve => setTimeout(resolve, 1000));
  return `You said: "${message}". This is a mock response because the API is not connected.`;
};

/**
 * Interface for the payload to send to the Llama realtime query endpoint
 */
export interface LlamaRealtimePayload {
  question: string;
  symbols?: string[];
}

/**
 * Interface for the response from the Llama realtime query endpoint
 */
export interface LlamaRealtimeResponse {
  success: boolean;
  response?: string;
  error?: string;
  model_used?: string;
  rag_sources_used?: string[];
}

/**
 * Enhanced response using financial LLM with Alpaca data
 * @param message User message
 * @param symbols Detected stock symbols
 * @returns Enhanced financial response
 */
export const enhancedFinancialResponse = async (message: string, symbols: string[]): Promise<LlamaRealtimeResponse> => {
  // Prepare payload for the financial LLM endpoint
  const payload: LlamaRealtimePayload = {
    question: message,
    symbols: symbols
  };
  
  return await sendLlamaRealtimeQuery(payload);
};

/**
 * Send a query to the Llama realtime endpoint with Alpaca market data integration
 * This uses real-time financial data from Alpaca to enhance LLM responses
 * 
 * @param payload Object containing question and optional symbols array
 * @returns Promise resolving to the LLM response with market data
 */
export const sendLlamaRealtimeQuery = async (payload: LlamaRealtimePayload): Promise<LlamaRealtimeResponse> => {
  try {
    console.log(`Sending Alpaca-enhanced LLM query: "${payload.question.substring(0, 50)}${payload.question.length > 50 ? '...' : ''}"`);
    console.log(`With symbols: ${payload.symbols?.join(', ') || 'none'}`);
    
    if (!payload.question) {
      return {
        success: false,
        error: 'Question is required',
      };
    }
    
    if (!payload.symbols || payload.symbols.length === 0) {
      return {
        success: false,
        error: 'No stock symbols provided',
      };
    }
    
    // Call the server endpoint that integrates Alpaca data with LLM
    try {
      console.log('Calling /api/chat/llama-query with timeout of 30 seconds');
      const response = await axios.post<LlamaRealtimeResponse>('/api/chat/llama-query', payload, {
        timeout: 60000, // Increase timeout to 60 seconds for complex financial analysis
      });
      
      if (response.data) {
        console.log('Received successful response from financial LLM');
        return response.data;
      } else {
        console.error('Empty response from LLM API');
        return {
          success: false,
          error: 'Received an empty response from the LLM service',
        };
      }
    } catch (axiosError) {
      console.error('Error sending Llama realtime query:', axiosError);
      
      // Check for CORS issues which might indicate mixed content problems
      if (axiosError.message && axiosError.message.includes('Network Error')) {
        console.error('Network error detected. This might be due to mixed content (HTTP/HTTPS) issues. Check the browser console for more details.');
        
        // Provide a more helpful response for network errors
        return {
          success: false,
          error: 'Network connectivity issue with financial data service',
          response: "I'm having trouble connecting to our financial data service. This could be due to network restrictions or a temporary service issue. Let me try to answer your question without real-time financial data."
        };
      }
      
      // Handle axios errors without using isAxiosError
      if (axiosError && typeof axiosError === 'object' && 'response' in axiosError) {
        const error = axiosError as { response?: { data?: any, status?: number } };
        console.error('API response error:', error.response?.data);
        
        // Add more detailed error message for debugging
        let errorMessage = 'Unknown server error';
        
        // Use the error response if available, otherwise construct a useful error
        if (error.response?.data) {
          // If we have a structured error response
          if (error.response.data.error) {
            errorMessage = typeof error.response.data.error === 'string' 
              ? (error.response.data.error.startsWith('{') && error.response.data.error.endsWith('}')
                  ? JSON.parse(error.response.data.error).message || error.response.data.error
                  : error.response.data.error)
              : JSON.stringify(error.response.data.error);
              
            // Check for specific API key errors
            if (
              errorMessage.includes('API key') || 
              errorMessage.includes('not configured') ||
              errorMessage.includes('credentials')
            ) {
              console.error('API key validation error detected');
              return {
                success: false,
                error: 'Financial data service API key issue',
                response: "I apologize, but our financial data service is currently experiencing credential issues. Your request has been logged, and we're working to resolve this. In the meantime, I'll try to answer without financial data enhancement."
              };
            }
          } else if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          }
        }
        
        // Fallback error message based on status code
        const statusMessage = error.response?.status
          ? `Server returned status ${error.response.status}`
          : 'Server error';
        
        console.error(`Financial LLM error details: ${statusMessage} - ${errorMessage}`);
        
        return {
          success: false,
          error: errorMessage || statusMessage,
          response: "I'm sorry, but I encountered an issue analyzing financial data. Please try again in a moment."
        };
      }
      
      // Last resort fallback for unknown errors
      return {
        success: false,
        error: axiosError instanceof Error ? `Unexpected error: ${axiosError.message}` : 'An unknown error occurred',
        response: "I apologize, but I'm having trouble connecting to our financial analysis service. Let me answer your question without the financial data."
      };
    }
  } catch (error) {
    // Catch any other errors that might occur
    console.error('Unexpected error in sendLlamaRealtimeQuery:', error);
    return {
      success: false,
      error: error instanceof Error ? `Unexpected error: ${error.message}` : 'An unknown error occurred',
      response: "I encountered an unexpected issue. Let me try to answer your question without financial data enhancement."
    };
  }
};
